import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/** 안전 현황 대시보드 (DOC-131 · 백엔드 {@code /api/v1/safe/dashboard}). */

export type SiteLevel = 'DANGER' | 'CAUTION' | 'GOOD' | 'ENDED';

export type DashboardSite = {
  siteId: string;
  siteName: string;
  level: SiteLevel;
  score: number;
  tbmToday: boolean;
  tbmSigners: number;
  workers: number;
  leadName: string | null;
  /** 현장 위치의 오늘 예보 최고기온(℃). 측정값이 아니다. */
  maxTemp: number | null;
  /** 「경고」(33℃↑) · 「위험」(35℃↑) · null. */
  heat: '경고' | '위험' | null;
  lastPatrol: string | null;
  /** 순회점검 주기(2일)를 넘긴 날 수. null 이면 기록 없음. */
  patrolLate: number | null;
  lastJoint: string | null;
  /** 합동점검 주기(2개월)를 넘긴 날 수. null 이면 기록 없음. */
  jointLate: number | null;
  openActions: number;
  overdueActions: number;
  openChecks: number;
  lastActivity: string | null;
  reasons: string[];
};

export type DashboardSummary = {
  totalSites: number;
  activeSites: number;
  tbmSites: number;
  heatWarn: number;
  heatDanger: number;
  heatKnown: number;
  legalSites: number;
  patrolOverdue: number;
  jointOverdue: number;
  openItems: number;
  levels: Record<SiteLevel, number>;
};

export type SafeDashboard = {
  today: string;
  generatedAt: string;
  summary: DashboardSummary;
  sites: DashboardSite[];
  daily: Array<{ day: string; tbmSites: number; patrols: number }>;
};

const BASE = '/v1/safe/dashboard';

export function useSafeDashboard() {
  return useQuery({
    queryKey: ['safe-dashboard'],
    queryFn: () => apiFetch<SafeDashboard>(BASE),
    // 하루 안에서 값이 바뀌는 것은 TBM · 일지 저장뿐이다. 열어 둔 화면은 5분마다 새로 받는다.
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * AI Agent 의 한 줄. 숫자 카드와 따로 받는다 — 에이전트를 기다리느라 화면이 늦게 뜨면 안 된다.
 * 서버가 같은 사실이면 다시 부르지 않으므로 여기서는 화면을 열 때 한 번만 묻는다.
 */
export function useSafeBriefing(enabled: boolean) {
  return useQuery({
    queryKey: ['safe-dashboard', 'briefing'],
    queryFn: () => apiFetch<{ available: boolean; text: string | null }>(`${BASE}/briefing`, { method: 'POST' }),
    enabled,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function isLegalLate(site: DashboardSite): boolean {
  return site.level !== 'ENDED' && (site.patrolLate === null || site.patrolLate > 0 || site.jointLate === null || site.jointLate > 0);
}
